import paper from 'paper/dist/paper-core'
import { CycleBoundaryDrawable } from '../../../shared/formwork-planner'
import { InteractionState } from '../../planner/types/interactionState'
import { LengthLabel } from '../../planner/model/paper/LengthLabel'
import { Cycle } from '../model/Cycle'

export enum CursorType {
  DEFAULT = 'default',
  POINTER = 'pointer',
}

export interface SelectedCycleSymbol {
  cyclePosition: paper.Point
  wallThickness: number
}

export interface CycleBoundaryInteractive {
  cycleBoundaryLabel?: CycleBoundaryDrawable | undefined
  state?: InteractionState | undefined // Can only be SELECTED, DRAG, or HOVER
}

/**
 * Responsible for the selection and hover effect on cycles and boundaries.
 * Creates and manages the HighlightAreas and the HighlightAreaLayer.
 */
export class CycleSelectionService {
  // Cycle Symbol Selection
  private _selectedCycleSymbol?: SelectedCycleSymbol
  public get selectedCycleSymbol(): SelectedCycleSymbol | undefined {
    return this._selectedCycleSymbol
  }

  // Cycle Boundary Dragging
  private _clickedCycleBoundaryForDrag?: CycleBoundaryDrawable | undefined
  public get clickedCycleBoundaryForDrag(): CycleBoundaryDrawable | undefined {
    return this._clickedCycleBoundaryForDrag
  }

  private _clickedCycleBoundaryForDragOriginal?: CycleBoundaryDrawable | undefined
  public get clickedCycleBoundaryForDragOriginal(): CycleBoundaryDrawable | undefined {
    return this._clickedCycleBoundaryForDragOriginal
  }

  // Cycle Boundary Selection
  private _selectedCycleBoundary: CycleBoundaryDrawable | undefined
  public get selectedCycleBoundary(): CycleBoundaryDrawable | undefined {
    return this._selectedCycleBoundary
  }

  // Length Label Selection
  private _selectedLengthLabelEdgeThickness?: number
  public get selectedLengthLabelEdgeThickness(): number | undefined {
    return this._selectedLengthLabelEdgeThickness
  }

  private _selectedLengthLabel: LengthLabel | undefined
  public get selectedLengthLabel(): LengthLabel | undefined {
    return this._selectedLengthLabel
  }

  private _selectedSnappingLine: paper.Path.Line | undefined
  public get selectedSnappingLine(): paper.Path.Line | undefined {
    return this._selectedSnappingLine
  }

  private _showBoundaryWithLengthLabel = false
  public get showBoundaryWithLengthLabel(): boolean {
    return this._showBoundaryWithLengthLabel
  }

  get isLengthLabelSelectionOpen(): boolean {
    return !!this.selectedLengthLabel && !!this.selectedCycleBoundary && !!this.selectedSnappingLine
  }

  // Selected Cycle For Preview
  private _selectedCycleForPreview: Cycle | undefined
  public get selectedCycleForPreview(): Cycle | undefined {
    return this._selectedCycleForPreview
  }

  private _previousSelectedCycleForPreview: Cycle | undefined

  // Constructor
  constructor() {}

  // Cycle Symbol Selection
  public setSelectedCycleSymbol(cyclePosition: paper.Point, wallThickness: number): void {
    this._selectedCycleSymbol = { cyclePosition, wallThickness }
  }

  public clearSelectedCycleSymbol(): void {
    this._selectedCycleSymbol = undefined
  }

  // Cycle Boundary Dragging
  public setClickedCycleBoundaryForDrag(cycleBoundary: CycleBoundaryDrawable): void {
    this._clickedCycleBoundaryForDrag = cycleBoundary
    this._clickedCycleBoundaryForDragOriginal = cycleBoundary.clone()
  }

  public clearClickedCycleBoundaryForDrag(): void {
    this._clickedCycleBoundaryForDrag = undefined
    this._clickedCycleBoundaryForDragOriginal = undefined
  }

  // Cycle Boundary Selection
  public setSelectedCycleBoundary(boundary: CycleBoundaryDrawable): void {
    this._selectedCycleBoundary = boundary
  }

  public clearSelectedCycleBoundary(): void {
    this._selectedCycleBoundary = undefined
  }

  // Length Label Selection
  public setSelectedLengthLabel(
    lengthLabel: LengthLabel,
    edgeThickness: number,
    snappingLine: paper.Path.Line,
    selectedCycleBoundary: CycleBoundaryDrawable,
    showBoundaryWithLengthLabel: boolean
  ): void {
    this._selectedLengthLabel = lengthLabel
    this._selectedLengthLabelEdgeThickness = edgeThickness
    this._selectedSnappingLine = snappingLine
    this._selectedCycleBoundary = selectedCycleBoundary
    this._showBoundaryWithLengthLabel = showBoundaryWithLengthLabel
  }

  public clearSelectedLengthLabel(): void {
    this._selectedLengthLabel = undefined
    this._selectedLengthLabelEdgeThickness = undefined
    this._selectedSnappingLine = undefined
    this._showBoundaryWithLengthLabel = false
  }

  // Selected Cycle For Preview
  public setSelectedCycleForPreview(cycle: Cycle): void {
    this._selectedCycleForPreview = cycle
  }

  public clearSelectedCycleForPreview(): void {
    this._selectedCycleForPreview = undefined
  }

  public setPreviousSelectedCycleForPreview(): void {
    if (!this.selectedCycleForPreview) return

    this._previousSelectedCycleForPreview = this.selectedCycleForPreview
  }

  public resetSelectedCycleForPreviewWithPrevious(): void {
    if (!this._previousSelectedCycleForPreview) return

    this._selectedCycleForPreview = this._previousSelectedCycleForPreview
    this._previousSelectedCycleForPreview = undefined
  }
}
